import {
  Alert, Box, Button, Checkbox, Container, FormControl, FormControlLabel,
  InputLabel, MenuItem, Paper, Select, Stack, TextField, Typography,
} from "@mui/material";
import axios from "axios";
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { accommodations, educationLevels, nationalities } from "../../consts";
import LanguageToggle from "../LanguageToggle/LanguageToggle";

const API = process.env.REACT_APP_API_URL;
const POLICY_VERSION = "v1.0-2025-08-25";

export default function CompleteGuestRegistration() {
  const { t, i18n } = useTranslation("ui");
  const isArabic = i18n.language === "ar";
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const email = useMemo(() => (params.get("email") || localStorage.getItem("placementGuestEmail") || "").trim().toLowerCase(), [params]);
  const [step, setStep] = useState("start");
  const [code, setCode] = useState("");
  const [completionToken, setCompletionToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    password: "", confirmPassword: "", dateOfBirth: "", nationality: "",
    accommodation: "", job: "", consentPrivacy: false, confirmAge: false,
  });

  const apiError = (err) => {
    const codeKey = err?.response?.data?.code;
    return codeKey
      ? t(`guestRegistration.errors.${codeKey}`, { defaultValue: t("guestRegistration.errors.generic") })
      : t("guestRegistration.errors.generic");
  };
  const start = async () => {
    setLoading(true); setError("");
    try {
      await axios.post(`${API}/api/users/guest-account/claim/start`, { email });
      setStep("verify");
    } catch (err) { setError(apiError(err)); } finally { setLoading(false); }
  };
  const verify = async () => {
    setLoading(true); setError("");
    try {
      const res = await axios.post(`${API}/api/users/guest-account/claim/verify`, { email, code });
      setCompletionToken(res.data.completionToken);
      setStep("complete");
    } catch (err) { setError(apiError(err)); } finally { setLoading(false); }
  };
  const complete = async () => {
    if (form.password !== form.confirmPassword) return setError(t("guestRegistration.errors.passwordMismatch"));
    setLoading(true); setError("");
    try {
      await axios.post(`${API}/api/users/guest-account/claim/complete`, {
        completionToken, password: form.password, dateOfBirth: form.dateOfBirth,
        nationality: form.nationality, accommodation: form.accommodation, job: form.job,
        consentPrivacy: form.consentPrivacy, confirmAge: form.confirmAge, policyVersion: POLICY_VERSION,
      });
      localStorage.removeItem("placementGuestEmail");
      setStep("done");
    } catch (err) { setError(apiError(err)); } finally { setLoading(false); }
  };
  const update = (event) => setForm((old) => ({ ...old, [event.target.name]: event.target.type === "checkbox" ? event.target.checked : event.target.value }));

  return (
    <Container maxWidth="sm" sx={{ py: 6 }} dir={isArabic ? "rtl" : "ltr"}>
      <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, textAlign: isArabic ? "right" : "left" }}>
        <Stack spacing={3}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}><LanguageToggle /></Box>
          <Typography variant="h4" fontWeight={800}>{t("guestRegistration.title")}</Typography>
          <Typography>{t("guestRegistration.intro")}</Typography>
          <TextField label={t("guestRegistration.email")} value={email} disabled fullWidth inputProps={{ dir: "ltr" }} />
          {error && <Alert severity="error">{error}</Alert>}
          {step === "start" && <Button disabled={loading || !email} variant="contained" onClick={start}>{t("guestRegistration.verifyEmail")}</Button>}
          {step === "verify" && <>
            <Alert severity="info">{t("guestRegistration.codeSent")}</Alert>
            <TextField label={t("guestRegistration.code")} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputProps={{ dir: "ltr" }} />
            <Button disabled={loading || code.length !== 6} variant="contained" onClick={verify}>{t("guestRegistration.continue")}</Button>
            <Button disabled={loading} onClick={start}>{t("guestRegistration.resend")}</Button>
          </>}
          {step === "complete" && <>
            <TextField name="password" type="password" label={t("guestRegistration.password")} value={form.password} onChange={update} required />
            <TextField name="confirmPassword" type="password" label={t("guestRegistration.confirmPassword")} value={form.confirmPassword} onChange={update} required />
            <TextField name="dateOfBirth" type="date" label={t("guestRegistration.dateOfBirth")} InputLabelProps={{ shrink: true }} value={form.dateOfBirth} onChange={update} required />
            <FormControl required><InputLabel>{t("guestRegistration.nationality")}</InputLabel><Select name="nationality" label={t("guestRegistration.nationality")} value={form.nationality} onChange={update}>{nationalities.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}</Select></FormControl>
            <FormControl required><InputLabel>{t("guestRegistration.residence")}</InputLabel><Select name="accommodation" label={t("guestRegistration.residence")} value={form.accommodation} onChange={update}>{accommodations.map((v) => <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>)}</Select></FormControl>
            <FormControl required><InputLabel>{t("guestRegistration.education")}</InputLabel><Select name="job" label={t("guestRegistration.education")} value={form.job} onChange={update}>{educationLevels.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}</Select></FormControl>
            <FormControlLabel control={<Checkbox name="consentPrivacy" checked={form.consentPrivacy} onChange={update} />} label={<span>{t("guestRegistration.acceptPrefix")} <Link to="/privacy-policy" target="_blank">{t("guestRegistration.privacyPolicy")}</Link>.</span>} />
            <FormControlLabel control={<Checkbox name="confirmAge" checked={form.confirmAge} onChange={update} />} label={t("guestRegistration.ageConfirm")} />
            <Button disabled={loading || !form.consentPrivacy || !form.confirmAge} variant="contained" onClick={complete}>{t("guestRegistration.createAccount")}</Button>
          </>}
          {step === "done" && <>
            <Alert severity="success">{t("guestRegistration.success")}</Alert>
            <Button variant="contained" onClick={() => navigate("/login")}>{t("guestRegistration.login")}</Button>
          </>}
          <Box><Button component={Link} to="/">{t("guestRegistration.backHome")}</Button></Box>
        </Stack>
      </Paper>
    </Container>
  );
}
